import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../components/CurrencySelector';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

export const CartPage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { format } = useCurrency();
  const { cart, loading, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="cart-page">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('yourCart')}</h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Connectez-vous pour voir votre panier' : 'Login to view your cart'}
          </p>
          <Button onClick={() => navigate('/login')} className="btn-primary">
            {t('login')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-4 bg-white p-4 rounded-xl">
                <Skeleton className="w-24 h-24 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-8 w-32" />
                </div>
              </div>
            ))}
          </div>
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="cart-page-empty">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('emptyCart')}</h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Découvrez nos produits' : 'Discover our products'}
          </p>
          <Button onClick={() => navigate('/shop')} className="btn-primary">
            {t('continueShopping')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Group items by vendor
  const itemsByVendor = cart.items.reduce((acc, item) => {
    const vendorId = item.vendor_id;
    if (!acc[vendorId]) {
      acc[vendorId] = {
        vendor_name: item.vendor_name,
        items: []
      };
    }
    acc[vendorId].items.push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cart-page">
      <div className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">{t('yourCart')}</h1>
          <Button variant="ghost" onClick={() => navigate('/shop')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('continueShopping')}
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {Object.entries(itemsByVendor).map(([vendorId, vendorData]) => (
              <div key={vendorId} className="bg-white rounded-xl border border-border/40 overflow-hidden">
                {/* Vendor Header */}
                <div className="bg-slate-50 px-6 py-3 border-b border-border/40">
                  <p className="font-medium text-sm text-muted-foreground">
                    {t('soldBy')}: <span className="text-[#0F4C5C]">{vendorData.vendor_name}</span>
                  </p>
                </div>
                
                {/* Items */}
                <div className="divide-y divide-border/40">
                  {vendorData.items.map((item) => (
                    <div key={item.product_id} className="p-6 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                      {/* Image */}
                      <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                        <img
                          src={item.image || 'https://via.placeholder.com/100'}
                          alt={getLocalizedName(item)}
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                      </Link>
                      
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${item.product_id}`} className="font-semibold hover:text-[#0F4C5C] transition-colors line-clamp-2">
                          {getLocalizedName(item)}
                        </Link>
                        
                        <p className="text-lg font-bold text-[#0F4C5C] mt-2">
                          {format(item.price)}
                        </p>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center border border-border rounded-lg">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-10 text-center font-medium">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => removeFromCart(item.product_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t('removeItem')}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Item Total */}
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {format(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Clear Cart */}
            <div className="flex justify-end">
              <Button variant="outline" onClick={clearCart} className="text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Vider le panier' : 'Clear cart'}
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-border/40 p-6 sticky top-32">
              <h2 className="text-lg font-bold mb-6">
                {language === 'fr' ? 'Récapitulatif' : 'Order Summary'}
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('subtotal')}</span>
                  <span className="font-medium">{format(cart.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('shipping')}</span>
                  <span className="font-medium text-green-600">
                    {cart.total >= 50 
                      ? (language === 'fr' ? 'Gratuit' : 'Free')
                      : format(4.99)}
                  </span>
                </div>
                <div className="border-t border-border pt-4 flex justify-between">
                  <span className="font-bold text-lg">{t('total')}</span>
                  <span className="font-bold text-lg text-[#0F4C5C]">
                    {format(cart.total + (cart.total >= 50 ? 0 : 4.99))}
                  </span>
                </div>
              </div>
              
              {cart.total < 50 && (
                <p className="text-sm text-muted-foreground mb-4 bg-[#E9C46A]/10 p-3 rounded-lg">
                  {language === 'fr' 
                    ? `Ajoutez ${format(50 - cart.total)} pour la livraison gratuite`
                    : `Add ${format(50 - cart.total)} for free shipping`}
                </p>
              )}
              
              <Button 
                className="w-full btn-primary h-12 text-lg"
                onClick={() => navigate('/checkout')}
                data-testid="checkout-btn"
              >
                {t('checkout')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                {language === 'fr' 
                  ? 'Paiement sécurisé par Stripe'
                  : 'Secure payment by Stripe'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
